import React, { useContext } from 'react';
import { ResponseError, SelectField, SubmitButton } from '@nintendo/forms';
import { Form, FormikProps } from 'formik';
import { Button, Modal } from 'react-bootstrap';
import ConfigContext from '../../../contexts/ConfigContext';
import useAsyncWithState from '../../../hooks/useAsyncWithState';
import { getUserContractSigners } from '../../../api/contractSignerApi';
import { ContractSignerType } from '../../../api/contractSignerApiTypes';
import { INITIAL_SELECT_ID, COMMENT_MAX_LENGTH } from '../../../utils/Constants';
import TextCountedField from '../TextCountedField';

export enum AmContractSignerFormType {
    ADD = 'ADD',
    REPLACE = 'REPLACE',
}

export interface AmContractSignerModalForm {
    signatureRequired: number;
    contractOrganizationId: number;
    contractSigner: number;
    contractSignerName: string;
    commentMessage: string;
}

export interface AmContractSignerFormProps extends FormikProps<AmContractSignerModalForm> {
    formType: AmContractSignerFormType;
    responseError: string;
    setResponseError: (errorMessage: string) => void;
    toggleModal: () => void;
}

const AmContractSignerForm = (props: AmContractSignerFormProps) => {
    const config = useContext(ConfigContext);
    const {
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        formType,
        responseError,
        toggleModal,
    } = props;

    const { value: contractSigners } = useAsyncWithState(
        () => getUserContractSigners(config),
        [] as ContractSignerType[],
    );
    const contractOrganizations = Array.from(
        new Map(
            contractSigners.map((contractSigner) => [
                contractSigner.organizationId,
                {
                    id: contractSigner.organizationId,
                    name: contractSigner.organizationName || '',
                },
            ]),
        ).values(),
    );

    let buttonText = 'Add';
    if (formType === AmContractSignerFormType.REPLACE) {
        if (values.signatureRequired > INITIAL_SELECT_ID) {
            buttonText = 'Replace';
        } else {
            buttonText = 'Remove';
        }
    }

    return (
        <>
            <Modal.Header closeButton>
                <Modal.Title>
                    {formType === AmContractSignerFormType.ADD && 'Add'}
                    {formType === AmContractSignerFormType.REPLACE && 'Replace'} Contract Signer
                </Modal.Title>
            </Modal.Header>
            <Form>
                <Modal.Body>
                    <ResponseError errorMessage={responseError} />
                    {formType === AmContractSignerFormType.REPLACE && (
                        <SelectField
                            label="Is there a legal document that requires a signature?"
                            name="signatureRequired"
                            options={[
                                {
                                    id: INITIAL_SELECT_ID,
                                    name: 'No',
                                },
                                {
                                    id: 1,
                                    name: 'Yes',
                                },
                            ]}
                            touched={touched.signatureRequired}
                            errors={errors.signatureRequired}
                            testId="signatureRequired"
                            isRequired
                            noDefault
                            onChange={() => {
                                setFieldValue('contractOrganizationId', INITIAL_SELECT_ID);
                                setFieldValue('contractSigner', INITIAL_SELECT_ID);
                            }}
                        />
                    )}
                    {values.signatureRequired > INITIAL_SELECT_ID && (
                        <SelectField
                            label="Contract Entity"
                            name="contractOrganizationId"
                            options={contractOrganizations}
                            touched={touched.contractOrganizationId}
                            errors={errors.contractOrganizationId}
                            testId="contractOrganizationId"
                            isRequired
                            tooltip="Which company is party to the agreement?"
                            onChange={() => {
                                setFieldValue('contractSigner', INITIAL_SELECT_ID);
                            }}
                        />
                    )}
                    {values.signatureRequired > INITIAL_SELECT_ID &&
                        values.contractOrganizationId &&
                        values.contractOrganizationId > INITIAL_SELECT_ID && (
                            <SelectField
                                label="Contract Signer"
                                name="contractSigner"
                                options={contractSigners
                                    .filter(
                                        (contractSigner) =>
                                            contractSigner.organizationId ===
                                            Number(values.contractOrganizationId),
                                    )
                                    .map((contractSigner) => ({
                                        id: contractSigner.personId,
                                        name: `${contractSigner.personName} (${contractSigner.title})`,
                                    }))}
                                touched={touched.contractSigner}
                                errors={errors.contractSigner}
                                testId="contractSigner"
                                isRequired
                                tooltip="Which officer should sign the attached legal document?"
                                onChange={(id: number) => {
                                    if (id > INITIAL_SELECT_ID) {
                                        const contractSigner = contractSigners.find(
                                            (value) => value.personId === id,
                                        );
                                        if (contractSigner) {
                                            setFieldValue(
                                                'contractSignerName',
                                                `${contractSigner.personName} (${contractSigner.title})`,
                                            );
                                        } else {
                                            setFieldValue('contractSignerName', '');
                                        }
                                    }
                                }}
                            />
                        )}
                    <TextCountedField
                        label="Comment (Optional)"
                        name="commentMessage"
                        touched={touched.commentMessage}
                        errors={errors.commentMessage}
                        testId="commentMessage"
                        length={values.commentMessage ? values.commentMessage.length : 0}
                        maxLength={COMMENT_MAX_LENGTH}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={toggleModal}
                        disabled={isSubmitting}
                    >
                        Close
                    </Button>
                    <SubmitButton
                        className="btn-primary ml-auto"
                        disabled={
                            values.signatureRequired > INITIAL_SELECT_ID &&
                            (values.contractOrganizationId === INITIAL_SELECT_ID ||
                                values.contractSigner === INITIAL_SELECT_ID)
                        }
                        isSubmitting={isSubmitting}
                        buttonText={buttonText}
                    />
                </Modal.Footer>
            </Form>
        </>
    );
};

export default AmContractSignerForm;
